
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate, Outlet } from 'react-router-dom';
import { Language } from '@/types';
import { Lock, ArrowRight, ShieldAlert } from 'lucide-react';
import { CONTENT } from '@/constants';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

import ScrollToTop from '@/components/global/ScrollToTop';
import GlobalErrorBoundary from '@/components/global/GlobalErrorBoundary';

import GlobalDropzone from '@/components/global/GlobalDropzone';
import NovaNavbar from '@/components/layout/NovaNavbar';
import CropLensNavbar from '@/components/layout/CropLensNavbar';
import AgroScoreNavbar from '@/components/layout/AgroScoreNavbar';


// Core Institutional & Marketing Pages (Statically imported for 0ms instantaneous navigation)
import Home from '@/pages/Home';
import Vision from '@/pages/Vision';
import Contact from '@/pages/Contact';
import SolutionsPage from '@/pages/SolutionsPage';
import SolutionDetailsPage from '@/pages/SolutionDetailsPage';
import CareersPage from '@/pages/CareersPage';
import { BrandPage } from '@/pages/BrandPage';
import { DeveloperPage } from '@/pages/DeveloperPage';
import { ResearchOverview } from '@/pages/ResearchOverview';
import { ResearchIndex } from '@/pages/ResearchIndex';
import { ResearchResidency } from '@/pages/ResearchResidency';
import { IntellectualPropertyPage } from '@/pages/IntellectualPropertyPage';
import { NewsroomPage } from '@/pages/NewsroomPage';
import AboutPage from '@/pages/AboutPage';
import NovaAboutPage from '@/pages/NovaAboutPage';
import NovaPricingPage from '@/pages/NovaPricingPage';
import NovaPage from '@/pages/NovaPage';
import GeoAgentPage from '@/pages/GeoAgentPage';
import CropLensPage from '@/pages/CropLensPage';
import DroughtWatchPage from '@/pages/DroughtWatchPage';
import AgriIntelligencePage from '@/pages/AgriIntelligencePage';
import { SafetyPage } from '@/pages/SafetyPage';
import HelpCenter from '@/pages/HelpCenter';
import ReleaseNotes from '@/pages/ReleaseNotes';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import TermsPolicies from '@/pages/TermsPolicies';
import SecurityPage from '@/pages/SecurityPage';
import LogoutPage from '@/pages/LogoutPage';


// Heavy Workspaces & Secondary Apps (Lazy Loaded)
const Studio = lazy(() => import('@/pages/Studio'));
const Network = lazy(() => import('@/pages/Network'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const PortalAccess = lazy(() => import('@/pages/PortalAccess'));
const OpenInDesktopPage = lazy(() => import('@/pages/OpenInDesktopPage'));
const CropLensPortal = lazy(() => import('@/pages/CropLensPortal'));
const AgroScorePage = lazy(() => import('@/pages/AgroScorePage'));
const AgroScoreGrowPage = lazy(() => import('@/pages/AgroScoreGrowPage'));
const AgroScoreFinancePage = lazy(() => import('@/pages/AgroScoreFinancePage'));
const AgroScoreGovernPage = lazy(() => import('@/pages/AgroScoreGovernPage'));
const AgroScorePortal = lazy(() => import('@/pages/AgroScorePortal'));
const CropLensVerificationPage = lazy(() => import('@/pages/CropLensVerificationPage'));
const GeoAIPage = lazy(() => import('@/features/geo-ai/GeoAIPage'));
const CropLensDashboard = lazy(() => import('@/features/croplens/CropLensDashboard'));
const AuthPage = lazy(() => import('@/features/auth/AuthPage').then(module => ({ default: module.AuthPage })));

const ClientDashboard = lazy(() => import('@/pages/ClientDashboard'));

const PricingPage = lazy(() => import('@/features/pricing/PricingPage'));
const SpatialMobileDocs = lazy(() => import('@/pages/SpatialMobileDocs'));
const NovaDesktopAppDocs = lazy(() => import('@/pages/NovaDesktopAppDocs'));
const PalantirIntegrationDocs = lazy(() => import('@/pages/PalantirIntegrationDocs'));
const DownloadPage = lazy(() => import('@/features/download/DownloadPage'));
const DesktopFeaturesPage = lazy(() => import('@/features/desktop/DesktopFeaturesPage'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const NovaWorkspace = lazy(() => import('@/pages/NovaWorkspace'));
const NovaCartographPage = lazy(() => import('@/pages/NovaCartographPage'));
const NovaChangelogPage = lazy(() => import('@/pages/NovaChangelogPage'));
const NovaDownloadPage = lazy(() => import('@/pages/NovaDownloadPage'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const ComingSoonPage = lazy(() => import('@/pages/ComingSoonPage'));
const BenchmarkPage = lazy(() => import('@/pages/BenchmarkPage'));
const DroughtWatchBenchmarkPage = lazy(() => import('@/pages/DroughtWatchBenchmarkPage'));

// Nova Docs
const NovaGettingStarted = lazy(() => import('@/features/nova-docs/pages/NovaGettingStarted'));
const NovaSpatialDataDocs = lazy(() => import('@/features/nova-docs/pages/NovaSpatialDataDocs'));
const NovaPromptGuide = lazy(() => import('@/features/nova-docs/pages/NovaPromptGuide'));
const NovaDocsMemory = lazy(() => import('@/features/nova-docs/pages/NovaDocsMemory'));
const NovaDocsChangelog = lazy(() => import('@/features/nova-docs/pages/NovaDocsChangelog'));
const NovaFAQ = lazy(() => import('@/features/nova-docs/pages/NovaFAQ'));
const NovaTroubleshooting = lazy(() => import('@/features/nova-docs/pages/NovaTroubleshooting'));
const NovaToolLibrary = lazy(() => import('@/features/nova-docs/pages/NovaToolLibrary'));
const NovaDocsManagingChats = lazy(() => import('@/features/nova-docs/pages/NovaDocsManagingChats'));
const NovaDocsTemporaryChats = lazy(() => import('@/features/nova-docs/pages/NovaDocsTemporaryChats'));
const NovaArcPyExecutionDocs = lazy(() => import('@/features/nova-docs/pages/NovaArcPyExecutionDocs'));
const NovaDeepResearchDocs = lazy(() => import('@/features/nova-docs/pages/NovaDeepResearchDocs'));
const NovaSpatialFilesDocs = lazy(() => import('@/features/nova-docs/pages/NovaSpatialFilesDocs'));
const NovaAcademicDocxDocs = lazy(() => import('@/features/nova-docs/pages/NovaAcademicDocxDocs'));
const NovaDesktopIntegrationDocs = lazy(() => import('@/features/nova-docs/pages/NovaDesktopIntegrationDocs'));
import NovaDocsLayout from '@/features/nova-docs/components/NovaDocsLayout';
import DocsPageSkeleton from '@/features/nova-docs/components/DocsPageSkeleton';




import { supabase } from '@/lib/supabase';
import { getCookie, setCookie, isProductPath } from '@/lib/utils';
import { Analytics } from '@vercel/analytics/react';

// SEO metadata for the application (used by react-helmet-async)
interface VexlSEOMetadata {
  title: string;
  description: string;
  keywords: string[];
  authors: Array<{ name: string }>;
  creator: string;
  publisher: string;
  openGraph: {
    type: string;
    locale: string;
    url: string;
    title: string;
    description: string;
    siteName: string;
  };
}

export const metadata: VexlSEOMetadata = {
  title: "VEXL | Spatial AI for GIS & Remote Sensing",
  description: "VEXL is an AI company building spatial intelligence systems for GIS, remote sensing, and real-world decision-making across education, agriculture, banking risk, cities, and national-scale operations.",
  keywords: ["Spatial AI", "GIS", "Remote Sensing", "GeoAI", "Spatial Modeling", "Geography AI"],
  authors: [{ name: "VEXL Studio" }],
  creator: "VEXL Studio",
  publisher: "VEXL Studio",
  openGraph: {
    type: "website",
    locale: "en_US",
    url: "https://vexl-gis.tech",
    title: "VEXL | Spatial AI for GIS & Remote Sensing",
    description: "AI systems for GIS, remote sensing, and spatial decision-making.",
    siteName: "VEXL",
  },
};

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SoftwareApplication",
      "name": "VEXL Nova",
      "operatingSystem": "Web",
      "applicationCategory": "DeveloperApplication, EducationalApplication",
      "description": "VEXL Nova is a student-centric Spatial Intelligence Layer and GeoAI assistant from VEXL, the parent AI company specialized in GIS and remote sensing. It helps students and researchers learn spatial modeling, write GIS code, and complete academic projects.",
      "creator": {
        "@type": "Organization",
        "name": "VEXL Studio",
        "founder": {
          "@type": "Person",
          "name": "Diaa Shousha",
          "jobTitle": "Principal GeoAI Architect"
        }
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Free academic tier available for verified .edu.eg students."
      },
      "featureList": [
        "Native Spatial Reasoning for GIS workflows",
        "Agentic Swarm execution for ArcPy and QGIS",
        "Bilingual Arabic/English technical DOCX generation",
        "Zero-Trust Academic Gating",
        "Project, Study, and Code execution modes"
      ]
    },
    {
      "@type": "Organization",
      "name": "VEXL Studio",
      "url": "https://vexl-gis.tech",
      "logo": "https://vexl-gis.tech/logo.png",
      "sameAs": [
        "https://www.linkedin.com/company/vexl-studio",
        "https://x.com/vexl_tech",
        "https://github.com/vexl-gis",
        "https://www.instagram.com/vexl_official/"
      ]
    }
  ]
};
// 🧠 Developer Easter Egg (Console Branding)
// Executed immediately at the top level to ensure it's the first message seen.
if (typeof window !== 'undefined') {
  console.info(
    `%c
 ██╗   ██╗███████╗██╗  ██╗██╗     
 ██║   ██║██╔════╝╚██╗██╔╝██║     
 ██║   ██║█████╗   ╚███╔╝ ██║     
 ╚██╗ ██╔╝██╔══╝   ██╔██╗ ██║     
  ╚████╔╝ ███████╗██╔╝ ██╗███████╗
   ╚═══╝  ╚══════╝╚═╝  ╚═╝╚══════╝
 __      ________  __ _      
 \\ \\    / /  ____|/ /| |     
  \\ \\  / /| |__  / /_| |     
   \\ \\/ / |  __||_  _| |     
    \\  /  | |____ | | | |____ 
     \\/   |______||_| |______|
                             
  SPATIAL AI ENGINE // v2.0
 
 Most people scroll. You inspect. Respect.
 
 Let's build something real.
 %cvexl.gis@gmail.com`,
    "color: #00FFC6; font-weight: bold; font-family: monospace;",
    "color: #888; font-family: sans-serif; font-size: 13px; line-height: 1.5;",
    "color: #00FFC6; font-weight: bold; text-decoration: underline;"
  );

  console.info(
    "%c Still here? You're definitely an engineer. %c Join the mission: vexl.gis@gmail.com ",
    "background: #00FFC6; color: #000; font-weight: bold; padding: 4px 8px; border-radius: 4px;",
    "color: #00FFC6; font-style: italic;"
  );
}

const CropLensGuard: React.FC<{ lang: Language }> = ({ lang }) => {
  const isAr = lang === 'ar';
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const isAuth = sessionStorage.getItem('croplens_auth') === 'true';
    if (isAuth) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const correctPassword = import.meta.env.VITE_CROPLENS_ACCESS_PASSWORD || 'vexl2026';
    if (password === correctPassword) {
      sessionStorage.setItem('croplens_auth', 'true');
      setIsAuthenticated(true);
      setError(false);
    } else {
      setError(true);
      setPassword('');
      setTimeout(() => setError(false), 800);
    }
  };

  if (isAuthenticated) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-[#060814] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background glowing gradients */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        .animate-shake {
          animation: shake 0.2s ease-in-out 0s 2;
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`w-full max-w-md liquid-glass p-8 space-y-6 relative border-white/10 ${error ? 'animate-shake border-red-500/30' : ''
          }`}
      >
        <div className="text-center space-y-4">
          <div className="w-16 h-16 liquid-glass-subtle rounded-full flex items-center justify-center mx-auto text-emerald-400 border border-white/5 shadow-[0_0_20px_rgba(16,185,129,0.1)]">
            <Lock className="w-6 h-6" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-white tracking-tight">
              {isAr ? 'بوابة مغلقة - CropLens' : 'Protected Portal - CropLens'}
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              {isAr
                ? 'هذه البوابة تحت التطوير والتدقيق حالياً. يرجى إدخال رمز المرور لتسجيل الدخول.'
                : 'This portal is currently under active development. Enter passcode to access.'}
            </p>
          </div>
        </div>

        <form onSubmit={handleVerify} className="space-y-4">
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isAr ? 'رمز المرور...' : 'Passcode...'}
              className="w-full liquid-glass-subtle pl-4 pr-12 py-3.5 rounded-xl text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500/40 focus:ring-1 focus:ring-emerald-500/20 transition-all font-mono tracking-wider"
              required
              autoFocus
            />
            <button
              type="submit"
              className="absolute right-2 top-2 p-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-all"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs text-red-400 font-semibold text-center flex items-center justify-center gap-1.5"
            >
              <ShieldAlert className="w-3.5 h-3.5" />
              {isAr ? 'رمز المرور غير صحيح. يرجى المحاولة مرة أخرى.' : 'Incorrect passcode. Please try again.'}
            </motion.p>
          )}
        </form>

        <div className="text-center pt-2">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">
            VEXL Spatio-Temporal Security Shield
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const SmartRedirect: React.FC<{ toPrefix: string; replacePrefix: string }> = ({ toPrefix, replacePrefix }) => {
  const location = useLocation();
  const newPathname = location.pathname.replace(replacePrefix, toPrefix);
  return <Navigate to={{ pathname: newPathname, search: location.search }} replace />;
};

const PageLoader: React.FC<{ lang?: Language }> = () => (
  <div className="fixed top-0 left-0 right-0 h-[2px] z-[9999] overflow-hidden pointer-events-none">
    <div className="w-full h-full bg-gradient-to-r from-transparent via-[#0E93A3] to-transparent animate-pulse" />
  </div>
);

const AnimatedRoutes: React.FC<{ lang: Language; setLang: (l: Language) => void; content: any; session: any }> = ({ lang, setLang, content, session }) => {
  const location = useLocation();
  const isAiShellRoute = location.pathname.startsWith('/ai') || location.pathname.startsWith('/products/ai');
  const isNovaShellRoute =
    location.pathname.startsWith('/nova-workspace') || location.pathname.startsWith('/nova/workspace');
  const isDocsRoute = location.pathname.startsWith('/nova/docs');

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes location={location} key={isAiShellRoute ? 'ai-terminal' : isNovaShellRoute ? 'nova-workspace' : isDocsRoute ? 'nova-docs' : location.pathname}>
        {/* Marketing Corporate Gateway (Root) */}
        <Route path="/" element={<Home content={content} lang={lang} session={session} />} />

        {/* Redirect old /home to root */}
        <Route path="/home" element={<Navigate to="/" replace />} />

        <Route path="/vision" element={<Vision content={content} lang={lang} />} />
        <Route path="/portal" element={<PortalAccess lang={lang} />} />
        <Route path="/portal/dashboard" element={<ClientDashboard lang={lang} />} />
        <Route path="/studio" element={<Studio content={content} lang={lang} />} />

        <Route path="/contact" element={<Contact lang={lang} content={content} />} />
        <Route path="/solutions" element={<SolutionsPage lang={lang} />} />
        <Route path="/solutions/:slug" element={<SolutionDetailsPage lang={lang} />} />
        <Route path="/agri-intelligence" element={<AgriIntelligencePage lang={lang} />} />
        <Route path="/safety" element={<SafetyPage lang={lang} />} />

        <Route path="/privacy" element={<PrivacyPolicy content={content} lang={lang} />} />
        <Route path="/terms" element={<TermsOfService content={content} lang={lang} />} />

        {/* Help & Documentation Routes */}
        <Route path="/help" element={<HelpCenter lang={lang} />} />
        <Route path="/release-notes" element={<ReleaseNotes lang={lang} />} />
        <Route path="/introducing-nova" element={<Navigate to="/benchmarks" replace />} />
        <Route path="/spatial-mobile" element={<SpatialMobileDocs lang={lang} />} />
        <Route path="/nova-desktop" element={<NovaDesktopAppDocs lang={lang} />} />
        <Route path="/palantir-integration" element={<PalantirIntegrationDocs lang={lang} />} />
        <Route path="/policies" element={<TermsPolicies lang={lang} />} />

        {/* VEXL Enterprise Portal Routes */}
        <Route path="/intellectual-property" element={<IntellectualPropertyPage lang={lang} />} />
        <Route path="/trust/ip" element={<Navigate to="/intellectual-property" replace />} />
        <Route path="/news" element={<NewsroomPage lang={lang} />} />
        <Route path="/brand" element={<BrandPage lang={lang} />} />
        <Route path="/careers" element={<CareersPage lang={lang} />} />
        <Route path="/research/overview" element={<ResearchOverview lang={lang} />} />
        <Route path="/research/index" element={<ResearchIndex lang={lang} />} />
        <Route path="/research/residency" element={<ResearchResidency lang={lang} />} />
        <Route path="/docs" element={<DeveloperPage lang={lang} />} />
        <Route path="/sdk" element={<DeveloperPage lang={lang} />} />
        <Route path="/about" element={<AboutPage lang={lang} />} />
        <Route path="/case-studies" element={<ComingSoonPage lang={lang} section="general" />} />
        <Route path="/security" element={<SecurityPage lang={lang} />} />
        <Route path="/trust/security" element={<Navigate to="/security" replace />} />
        <Route path="/integrations" element={<ComingSoonPage lang={lang} section="general" />} />
        <Route path="/benchmarks" element={<BenchmarkPage lang={lang} />} />
        <Route path="/benchmark" element={<BenchmarkPage lang={lang} />} />
        <Route path="/droughtwatch/benchmark" element={<DroughtWatchBenchmarkPage lang={lang} />} />
        <Route path="/droughtwatch-benchmark" element={<DroughtWatchBenchmarkPage lang={lang} />} />
        <Route path="/map" element={<Navigate to="/nova-workspace/map" replace />} />
        <Route path="/sandbox" element={<Navigate to="/nova-workspace/map" replace />} />
        <Route path="/spatial/sandbox" element={<Navigate to="/nova-workspace/map" replace />} />
        <Route path="/partners" element={<ComingSoonPage lang={lang} section="general" />} />
        <Route path="/changelog" element={<Navigate to="/release-notes" replace />} />

        {/* Auth Route */}
        <Route path="/login" element={<AuthPage lang={lang} />} />
        <Route path="/auth/logout" element={<LogoutPage lang={lang} />} />
        <Route path="/logout" element={<Navigate to="/auth/logout" replace />} />
        {/* VEXL-style Desktop Deep Link Redirect */}
        <Route path="/auth/desktop" element={<OpenInDesktopPage />} />



        {/* Pricing Route */}
        <Route path="/pricing" element={<Navigate to="/nova/pricing" replace />} />
        <Route path="/upgrade" element={<Navigate to="/nova/pricing" replace />} />
        <Route path="/payment-success" element={<PaymentSuccess lang={lang} session={session} />} />

        {/* Download & Feature Routes */}
        <Route path="/download" element={<Navigate to="/" replace />} />
        <Route path="/features/desktop" element={<DesktopFeaturesPage lang={lang} />} />

        {/* ─── Arabic Localized Marketing Routes (/ar/*) ─── */}
        <Route path="/ar" element={<Outlet />}>
          <Route index element={<Home content={CONTENT['ar']} lang="ar" session={session} />} />
          <Route path="home" element={<Navigate to="/ar" replace />} />
          <Route path="vision" element={<Vision content={CONTENT['ar']} lang="ar" />} />
          <Route path="portal" element={<PortalAccess lang="ar" />} />
          <Route path="portal/dashboard" element={<ClientDashboard lang="ar" />} />
          <Route path="studio" element={<Studio content={CONTENT['ar']} lang="ar" />} />
          <Route path="contact" element={<Contact lang="ar" content={CONTENT['ar']} />} />
          <Route path="solutions" element={<SolutionsPage lang="ar" />} />
          <Route path="solutions/:slug" element={<SolutionDetailsPage lang="ar" />} />
          <Route path="agri-intelligence" element={<AgriIntelligencePage lang="ar" />} />
          <Route path="safety" element={<SafetyPage lang="ar" />} />
          <Route path="privacy" element={<PrivacyPolicy content={CONTENT['ar']} lang="ar" />} />
          <Route path="terms" element={<TermsOfService content={CONTENT['ar']} lang="ar" />} />
          <Route path="policies" element={<TermsPolicies lang="ar" />} />
          <Route path="help" element={<HelpCenter lang="ar" />} />
          <Route path="release-notes" element={<ReleaseNotes lang="ar" />} />
          <Route path="introducing-nova" element={<Navigate to="/ar/benchmarks" replace />} />
          <Route path="spatial-mobile" element={<SpatialMobileDocs lang="ar" />} />
          <Route path="nova-desktop" element={<NovaDesktopAppDocs lang="ar" />} />
          <Route path="palantir-integration" element={<PalantirIntegrationDocs lang="ar" />} />
          <Route path="intellectual-property" element={<IntellectualPropertyPage lang="ar" />} />
          <Route path="trust/ip" element={<Navigate to="/ar/intellectual-property" replace />} />
          <Route path="news" element={<NewsroomPage lang="ar" />} />
          <Route path="brand" element={<BrandPage lang="ar" />} />
          <Route path="careers" element={<CareersPage lang="ar" />} />
          <Route path="research/overview" element={<ResearchOverview lang="ar" />} />
          <Route path="research/index" element={<ResearchIndex lang="ar" />} />
          <Route path="research/residency" element={<ResearchResidency lang="ar" />} />
          <Route path="docs" element={<DeveloperPage lang="ar" />} />
          <Route path="sdk" element={<DeveloperPage lang="ar" />} />
          <Route path="about" element={<AboutPage lang="ar" />} />
          <Route path="case-studies" element={<ComingSoonPage lang="ar" section="general" />} />
          <Route path="security" element={<SecurityPage lang="ar" />} />
          <Route path="trust/security" element={<Navigate to="/ar/security" replace />} />
          <Route path="integrations" element={<ComingSoonPage lang="ar" section="general" />} />
          <Route path="benchmarks" element={<BenchmarkPage lang="ar" />} />
          <Route path="benchmark" element={<BenchmarkPage lang="ar" />} />
          <Route path="droughtwatch/benchmark" element={<DroughtWatchBenchmarkPage lang="ar" />} />
          <Route path="droughtwatch-benchmark" element={<DroughtWatchBenchmarkPage lang="ar" />} />
          <Route path="partners" element={<ComingSoonPage lang="ar" section="general" />} />
          <Route path="changelog" element={<Navigate to="/ar/release-notes" replace />} />
          <Route path="payment-success" element={<PaymentSuccess lang="ar" session={session} />} />
          <Route path="features/desktop" element={<DesktopFeaturesPage lang="ar" />} />

          {/* Strict Product Exclusion: Redirect any product path entered under /ar directly to unprefixed LTR product path */}
          <Route path="products/*" element={<SmartRedirect toPrefix="/products" replacePrefix="/ar/products" />} />
          <Route path="nova/*" element={<SmartRedirect toPrefix="/nova" replacePrefix="/ar/nova" />} />
          <Route path="nova-workspace/*" element={<SmartRedirect toPrefix="/nova-workspace" replacePrefix="/ar/nova-workspace" />} />
          <Route path="croplens/*" element={<SmartRedirect toPrefix="/croplens" replacePrefix="/ar/croplens" />} />
          <Route path="agroscore/*" element={<SmartRedirect toPrefix="/agroscore" replacePrefix="/ar/agroscore" />} />
          <Route path="droughtwatch/*" element={<SmartRedirect toPrefix="/products/droughtwatch" replacePrefix="/ar/droughtwatch" />} />
          <Route path="ai/*" element={<Navigate to="/nova-workspace" replace />} />
          <Route path="geoai" element={<Navigate to="/products/geo" replace />} />
          <Route path="*" element={<NotFound lang="ar" />} />
        </Route>


        {/* The New Enterprise OS Ecosystem */}

        <Route
          path="/products/nova"
          element={<NovaPage lang={lang} />}
        />
        <Route
          path="/nova/pricing"
          element={<NovaPricingPage lang={lang} />}
        />
        <Route
          path="/nova/about"
          element={<NovaAboutPage lang={lang} />}
        />
        <Route
          path="/nova/download"
          element={<NovaDownloadPage lang={lang} />}
        />
        <Route
          path="/nova/changelog"
          element={<Navigate to="/nova/docs/changelog" replace />}
        />
        <Route
          path="/nova/changelog/v1-0-genesis"
          element={<Navigate to="/nova/docs/changelog" replace />}
        />
        <Route
          path="/nova/changelog/v1-1-dynamic-personas"
          element={<Navigate to="/nova/docs/changelog" replace />}
        />

        {/* Nova Documentation — Persistent Shell with Instant Outlet Transition & Skeleton Fallback */}
        <Route path="/nova/docs" element={<NovaDocsLayout lang={lang} />}>
          <Route index element={<Navigate to="/nova/docs/getting-started" replace />} />
          <Route path="getting-started" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaGettingStarted lang={lang} /></Suspense>} />
          <Route path="spatial-data" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaSpatialDataDocs lang={lang} /></Suspense>} />
          <Route path="prompt-guide" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaPromptGuide lang={lang} /></Suspense>} />
          <Route path="memory" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaDocsMemory lang={lang} /></Suspense>} />
          <Route path="changelog" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaDocsChangelog lang={lang} /></Suspense>} />
          <Route path="faq" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaFAQ lang={lang} /></Suspense>} />
          <Route path="troubleshooting" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaTroubleshooting lang={lang} /></Suspense>} />
          <Route path="tools" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaToolLibrary lang={lang} /></Suspense>} />
          <Route path="managing-chats" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaDocsManagingChats lang={lang} /></Suspense>} />
          <Route path="temporary-chats" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaDocsTemporaryChats lang={lang} /></Suspense>} />
          <Route path="arcpy-execution" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaArcPyExecutionDocs lang={lang} /></Suspense>} />
          <Route path="deep-research" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaDeepResearchDocs lang={lang} /></Suspense>} />
          <Route path="spatial-files" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaSpatialFilesDocs lang={lang} /></Suspense>} />
          <Route path="docx-generator" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaAcademicDocxDocs lang={lang} /></Suspense>} />
          <Route path="desktop-integration" element={<Suspense fallback={<DocsPageSkeleton isRtl={lang === 'ar'} />}><NovaDesktopIntegrationDocs lang={lang} /></Suspense>} />
        </Route>




        <Route path="/nova/workspace" element={<Navigate to="/nova-workspace" replace />} />
        <Route path="/nova/cartograph" element={<Navigate to="/nova-cartograph" replace />} />
        <Route
          path="/nova-cartograph"
          element={
            <GlobalErrorBoundary surface="Nova Cartograph">
              <NovaCartographPage />
            </GlobalErrorBoundary>
          }
        />
        {/* Nova Workspace — Public Guest Landing */}
        <Route
          path="/nova-workspace"
          element={
            <GlobalErrorBoundary surface="Nova Workspace">
              <NovaWorkspace lang={lang} setLang={setLang} session={session} />
            </GlobalErrorBoundary>
          }
        />

        {/* Protected Nova Routes — Authenticated Users Only */}
        <Route
          path="/nova-workspace/c/:chatId"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
            )
          }
        />
        <Route
          path="/nova-workspace/chat"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to="/login?redirect=/nova-workspace/chat" replace />
            )
          }
        />
        <Route
          path="/nova-workspace/chat/:chatId"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
            )
          }
        />
        <Route
          path="/nova-workspace/incognito"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to="/login?redirect=/nova-workspace/incognito" replace />
            )
          }
        />
        <Route
          path="/nova-workspace/projects"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to="/login?redirect=/nova-workspace/projects" replace />
            )
          }
        />
        <Route
          path="/nova-workspace/projects/:projectId"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
            )
          }
        />
        <Route
          path="/nova-workspace/reports"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to="/login?redirect=/nova-workspace/reports" replace />
            )
          }
        />
        <Route
          path="/nova-workspace/reports/:reportId"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
            )
          }
        />
        <Route
          path="/nova-workspace/library"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace Library">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to="/login?redirect=/nova-workspace/library" replace />
            )
          }
        />
        <Route path="/library" element={<Navigate to="/nova-workspace/library" replace />} />
        <Route
          path="/nova-workspace/map"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace Map">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to="/login?redirect=/nova-workspace/map" replace />
            )
          }
        />
        <Route
          path="/nova-workspace/map/:mapId"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace Map">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
            )
          }
        />
        <Route
          path="/nova-workspace/sandbox"
          element={
            session?.user ? (
              <GlobalErrorBoundary surface="Nova Workspace Map">
                <NovaWorkspace lang={lang} setLang={setLang} session={session} />
              </GlobalErrorBoundary>
            ) : (
              <Navigate to="/login?redirect=/nova-workspace/map" replace />
            )
          }
        />

        {/* /products/ai → Redirected to Nova (VexlGPT is Legacy) */}
        <Route path="/products/ai" element={<Navigate to="/nova-workspace" replace />} />
        <Route path="/products/ai/c/:chatId" element={<Navigate to="/nova-workspace" replace />} />

        <Route
          path="/products/geo"
          element={<GeoAgentPage lang={lang} />}
        />
        <Route
          path="/products/droughtwatch"
          element={<DroughtWatchPage lang={lang} />}
        />
        <Route
          path="/products/agroscore"
          element={<AgroScorePage lang={lang} />}
        />
        <Route
          path="/products/agroscore/grow"
          element={<AgroScoreGrowPage lang={lang} />}
        />
        <Route
          path="/agroscore/grow"
          element={<Navigate to="/products/agroscore/grow" replace />}
        />
        <Route
          path="/products/grow"
          element={<Navigate to="/products/agroscore/grow" replace />}
        />
        <Route
          path="/products/agroscore/finance"
          element={<AgroScoreFinancePage lang={lang} />}
        />
        <Route
          path="/agroscore/finance"
          element={<Navigate to="/products/agroscore/finance" replace />}
        />
        <Route
          path="/products/finance"
          element={<Navigate to="/products/agroscore/finance" replace />}
        />
        <Route
          path="/products/agroscore/govern"
          element={<AgroScoreGovernPage lang={lang} />}
        />
        <Route
          path="/agroscore/govern"
          element={<Navigate to="/products/agroscore/govern" replace />}
        />
        <Route
          path="/products/govern"
          element={<Navigate to="/products/agroscore/govern" replace />}
        />
        <Route
          path="/agroscore"
          element={<Navigate to="/products/agroscore" replace />}
        />
        <Route
          path="/products/agroscore/workspace"
          element={<Navigate to="/products/agroscore/workspace/analyst" replace />}
        />
        <Route
          path="/products/agroscore/workspace/:tab"
          element={<AgroScorePortal lang={lang} />}
        />
        <Route
          path="/agroscore/workspace"
          element={<Navigate to="/products/agroscore/workspace/analyst" replace />}
        />
        <Route
          path="/agroscore/workspace/:tab"
          element={<SmartRedirect toPrefix="/products/agroscore/workspace" replacePrefix="/agroscore/workspace" />}
        />
        <Route
          path="/droughtwatch"
          element={<Navigate to="/products/droughtwatch" replace />}
        />

        {/* Protected CropLens Routes Group */}
        <Route element={<CropLensGuard lang={lang} />}>
          <Route
            path="/products/croplens"
            element={<CropLensPage lang={lang} />}
          />
          <Route
            path="/products/croplens/workspace"
            element={<Navigate to="/products/croplens/workspace/farmer" replace />}
          />
          <Route
            path="/products/croplens/workspace/:tab"
            element={<CropLensPortal lang={lang} />}
          />
          <Route
            path="/croplens"
            element={<Navigate to="/products/croplens" replace />}
          />
          <Route
            path="/croplens/workspace"
            element={<Navigate to="/products/croplens/workspace/farmer" replace />}
          />
          <Route
            path="/croplens/workspace/:tab"
            element={<SmartRedirect toPrefix="/products/croplens/workspace" replacePrefix="/croplens/workspace" />}
          />
          <Route
            path="/croplens/verify/:claimId"
            element={<CropLensVerificationPage lang={lang} />}
          />
          <Route
            path="/products/croplens/verify/:claimId"
            element={<CropLensVerificationPage lang={lang} />}
          />
        </Route>


        {/* Legacy Redirects — /ai → Nova Workspace */}
        <Route path="/ai" element={<Navigate to="/nova-workspace" replace />} />
        <Route path="/ai/c/:chatId" element={<Navigate to="/nova-workspace" replace />} />

        <Route path="/geoai" element={<SmartRedirect toPrefix="/products/geo" replacePrefix="/geoai" />} />

        {/* Admin Dashboard */}
        <Route path="/admin" element={<AdminDashboard />} />

        <Route path="*" element={<NotFound lang={lang} />} />
      </Routes>
    </Suspense>
  );
};

const LayoutManager: React.FC<{
  lang: Language;
  content: any;
  session: any;
  isAuthLoading: boolean;
  isLoading: boolean;
  theme: any;
  setLang: (l: Language) => void;
  toggleTheme: any;
  isRtl: boolean;
}> = ({ lang, content, session, isAuthLoading, isLoading, theme, setLang, toggleTheme }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isNovaWorkspaceRoute =
    location.pathname.startsWith('/nova-workspace') ||
    location.pathname.startsWith('/nova/workspace') ||
    location.pathname.startsWith('/nova-cartograph') ||
    location.pathname.startsWith('/nova/cartograph');
  const isDesktopApp =
    location.pathname === '/geoai' ||
    location.pathname === '/ai' ||
    location.pathname.startsWith('/ai/c/') ||
    isNovaWorkspaceRoute;

  const isArRoute = location.pathname === '/ar' || location.pathname.startsWith('/ar/');
  const isProduct = isProductPath(location.pathname) || isDesktopApp;

  // Language & Direction Resolution:
  // - On product routes: internal state determines language, but layout is ALWAYS strictly LTR.
  // - On marketing routes: /ar prefix determines Arabic and enables RTL; otherwise English and LTR.
  const effectiveLang: Language = isProduct ? lang : (isArRoute ? 'ar' : 'en');
  const effectiveContent = CONTENT[effectiveLang];
  const effectiveIsRtl = !isProduct && effectiveLang === 'ar';

  // Synchronize document attributes dynamically based on current route
  useEffect(() => {
    document.documentElement.dir = effectiveIsRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = effectiveLang;
  }, [effectiveIsRtl, effectiveLang]);

  const handleSetLang = (newLang: Language) => {
    setLang(newLang);

    // Strict Product Exclusion: Never alter URL on product routes!
    if (isProduct) {
      return;
    }

    if (newLang === 'ar') {
      if (!isArRoute) {
        const targetPath = location.pathname === '/' ? '/ar' : `/ar${location.pathname}`;
        navigate({ pathname: targetPath, search: location.search, hash: location.hash });
      }
    } else {
      if (isArRoute) {
        const targetPath = location.pathname === '/ar' || location.pathname === '/ar/'
          ? '/'
          : location.pathname.replace(/^\/ar/, '');
        navigate({ pathname: targetPath || '/', search: location.search, hash: location.hash });
      }
    }
  };

  if (isDesktopApp) {
    if (isAuthLoading) {
      return (
        <div className="fixed inset-0 w-full h-[100dvh] bg-[#09090b] flex flex-col items-center justify-center gap-6" dir="ltr">
          <div className="flex items-center gap-4 relative origin-center">
            <div className="w-1.5 h-12 bg-gradient-to-b from-cyan-400 to-cyan-700 rounded-sm shadow-[0_0_20px_rgba(6,182,212,0.5)] animate-pulse" />
            <h1 className="text-5xl font-black text-white tracking-[0.2em] font-sans m-0 leading-none">
              VEXL
            </h1>
          </div>
          <div className="flex flex-col items-center gap-1">
            <p className="text-slate-500 font-mono text-[10px] uppercase tracking-[0.4em] animate-pulse">
              {effectiveLang === 'ar' ? 'تأمين مساحة العمل...' : 'Securing Workspace...'}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div
        className={`fixed inset-0 w-full h-[100dvh] overflow-hidden ${isNovaWorkspaceRoute
          ? 'bg-[#0d0d10] text-slate-100 selection:bg-[#7F77DD]/30 selection:text-white'
          : 'bg-[#030303] text-white selection:bg-cyan-500/30 selection:text-white'
          } ${effectiveIsRtl ? 'font-arabic' : 'font-sans'}`}
        dir="ltr"
      >
        <AnimatedRoutes lang={effectiveLang} setLang={handleSetLang} content={effectiveContent} session={session} />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-slate-50 dark:bg-black text-slate-900 dark:text-white selection:bg-cyan-500/30 selection:text-white relative transition-colors duration-500 ${effectiveIsRtl ? 'font-arabic' : 'font-sans'}`}
      dir={effectiveIsRtl ? 'rtl' : 'ltr'}
    >
      {/* 3. Main Layout */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Global Navbar */}
        {!isLoading && !location.pathname.startsWith('/nova') && !location.pathname.startsWith('/products/nova') && !location.pathname.startsWith('/nova-workspace') && !location.pathname.startsWith('/croplens') && !location.pathname.startsWith('/products/croplens') && !location.pathname.startsWith('/agroscore') && !location.pathname.startsWith('/products/agroscore') && !['/ai'].includes(location.pathname) && (
          <Navbar lang={effectiveLang} setLang={handleSetLang} theme={theme} toggleTheme={toggleTheme} content={effectiveContent.nav} isLoading={isLoading} />
        )}

        {/* Nova Specific Navbar (Shared across all /nova routes to prevent flickering) */}
        {!isLoading && (location.pathname.startsWith('/nova') || location.pathname.startsWith('/products/nova')) && !location.pathname.includes('workspace') && (
          <NovaNavbar lang={effectiveLang} />
        )}

        {/* CropLens Specific Navbar */}
        {!isLoading && (location.pathname.startsWith('/croplens') || location.pathname.startsWith('/products/croplens')) && (
          <CropLensNavbar lang={effectiveLang} />
        )}

        {/* AgroScore Specific Navbar */}
        {!isLoading && (location.pathname.startsWith('/agroscore') || location.pathname.startsWith('/products/agroscore')) && (
          <AgroScoreNavbar lang={effectiveLang} setLang={handleSetLang} />
        )}

        <main className="flex-1 flex flex-col min-h-[calc(100vh-80px)] w-full relative">
          <AnimatedRoutes lang={effectiveLang} setLang={handleSetLang} content={effectiveContent} session={session} />
        </main>

        {location.pathname !== '/brand' && !location.pathname.startsWith('/nova/docs') && !location.pathname.startsWith('/nova-workspace') && (
          <Footer 
            content={effectiveContent.footer} 
            lang={effectiveLang} 
            setLang={handleSetLang}
            theme={theme}
            toggleTheme={toggleTheme}
            showLogo={!isLoading} 
          />
        )}

      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [lang, setLangState] = useState<Language>(() => {
    const saved = getCookie('vexl_lang');
    return (saved === 'ar' || saved === 'en') ? saved : 'en';
  });

  const setLang = (newLang: Language) => {
    setLangState(newLang);
    setCookie('vexl_lang', newLang, 365);
    try {
      localStorage.setItem('vexl_lang', newLang);
    } catch {}
  };


  // 🧠 Force Initial Load on Mount for Splash Screen
  const [isLoading, setIsLoading] = useState(false);

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) return savedTheme as 'light' | 'dark';
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    }
    return 'dark';
  });

  const content = CONTENT[lang];
  const isRtl = lang === 'ar';

  const [session, setSession] = useState<any>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    // ─── Official Deep Link Auth Bridge ───────────────────────────────────────
    let cleanupDeepLink: (() => Promise<void>) | null = null;

    const setupDeepLink = async () => {
      try {
        const { onOpenUrl, getCurrent } = await import('@tauri-apps/plugin-deep-link');

        const handleDeepLinkUrl = async (urls: string[]) => {
          for (const urlStr of urls) {
            if (urlStr.startsWith('vexl://auth')) {
              const cleanUrl = urlStr.replace('vexl://', 'http://localhost/');
              const url = new URL(cleanUrl);
              const accessToken = url.searchParams.get('access_token');
              const refreshToken = url.searchParams.get('refresh_token');

              if (accessToken && refreshToken) {
                const { error } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken,
                });
                if (error) {
                  // Silently ignore or handle connection error
                }
              }
              break;
            }
          }
        };

        // Handle URLs that open the app from a cold start
        const startupUrls = await getCurrent();
        if (startupUrls && startupUrls.length > 0) {
          await handleDeepLinkUrl(startupUrls);
        }

        // Handle URLs while the app is already running
        const unlisten = await onOpenUrl(handleDeepLinkUrl);
        cleanupDeepLink = unlisten as any;
      } catch (err) {
        // Silently skip if not running inside Tauri container
      }
    };

    setupDeepLink();

    // ─── Telemetry Context Sync ───────────────────────────────────────────────
    const handleAuthChange = async (newSession: any) => {
      if (newSession?.user) {
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('subscription_tier, tier, tokens_balance, is_admin')
            .eq('id', newSession.user.id)
            .single();

          const { resolveProfileTier, setCachedProfile, getCachedProfile } = await import('@/features/nova-workspace/types');
          const resolvedProfile = profile ? {
            tokens_balance: profile.tokens_balance ?? 0,
            subscription_tier: profile.subscription_tier,
            tier: profile.tier,
            is_admin: profile.is_admin ?? false,
          } : null;
          if (resolvedProfile) {
            setCachedProfile(newSession.user.id, resolvedProfile);
          }
          const resolvedTier = resolveProfileTier(resolvedProfile);

          const { setUserContext } = await import('@/lib/observability');
          setUserContext(newSession.user.id, newSession.user.email || '', resolvedTier);
        } catch (err) {
          console.warn('[Auth] Failed to load user profile for telemetry context, checking cache:', err);
          const { getCachedProfile, resolveProfileTier } = await import('@/features/nova-workspace/types');
          const cached = getCachedProfile(newSession.user.id);
          const fallbackTier = resolveProfileTier(cached);
          const { setUserContext } = await import('@/lib/observability');
          setUserContext(newSession.user.id, newSession.user.email || '', fallbackTier);
        }
      } else {
        try {
          const { clearUserContext } = await import('@/lib/observability');
          clearUserContext();
        } catch (err) {
          console.warn('[Auth] Failed to clear user context:', err);
        }
      }
    };

    // ─── Supabase Auth State ──────────────────────────────────────────────────
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsAuthLoading(false);
      handleAuthChange(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // TOKEN_REFRESHED = silent JWT rotation. User identity is the same.
      // Skipping prevents a new session object from cascading a full App re-render
      // and wiping in-progress chat/canvas state in all products pages.
      if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') return;
      setSession(session);
      handleAuthChange(session);
    });

    return () => {
      if (cleanupDeepLink) cleanupDeepLink();
      subscription.unsubscribe();
    };
  }, []);



  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };



  return (
    <Router>
      <Analytics debug={false} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <ScrollToTop />
      <LayoutManager
        lang={lang}
        content={content}
        session={session}
        isAuthLoading={isAuthLoading}
        isLoading={isLoading}
        theme={theme}
        setLang={setLang}
        toggleTheme={toggleTheme}
        isRtl={isRtl}
      />
    </Router>
  );
};

export default App;
